import { useEffect, useMemo, useState } from "react";
import { useToast } from "@chakra-ui/react";
import useAxios from "axios-hooks";

/**
 * Headless component for Profile Pricing logic
 * Manages all state and business logic for pricing management
 */
const HL_ProfilePricing = ({ id, data, onRefresh, children }) => {
  const toast = useToast();

  // State management - Three clear states
  const [searchQuery, setSearchQuery] = useState("");
  const [editMode, setEditMode] = useState({});
  const [expandedItems, setExpandedItems] = useState([]);

  // Default state: Original pricing from backend (baseline)
  const [defaultState, setDefaultState] = useState({});

  // Updated state: Stations with modified pricing
  const [updatedState, setUpdatedState] = useState({});

  // Deleted state: Stations with custom pricing removed
  const [deletedState, setDeletedState] = useState({});

  // API hook for updating pricing
  const [{ loading: updateLoading }, updatePricing] = useAxios(
    {
      url: `/api/userprofiles/${id}/stationinfos`,
      method: "POST",
    },
    { manual: true }
  );

  // Parse data
  const actualData = data?.ActualData || [];
  const pricingData = data?.PricingData || [];

  // Initialize default state from backend data
  useEffect(() => {
    const defaultMap = {};
    pricingData.forEach((pricingLocation) => {
      pricingLocation.Stations?.forEach((station) => {
        // Use station's LocationId and station's Id
        const key = `${station.LocationId}-${station.Id}`;
        defaultMap[key] = {
          locationId: station.LocationId,
          stationId: station.Id,
          price: station.PricePerUnit,
        };
      });
    });
    setDefaultState(defaultMap);
  }, [pricingData]);

  // Check if a station is in default state (has custom pricing from backend)
  const isInDefaultState = (locationId, stationId) => {
    const key = `${locationId}-${stationId}`;
    return defaultState[key] !== undefined;
  };

  // Check if a station is in updated state
  const isInUpdatedState = (locationId, stationId) => {
    const key = `${locationId}-${stationId}`;
    return updatedState[key] !== undefined;
  };

  // Check if a station is in deleted state
  const isInDeletedState = (locationId, stationId) => {
    const key = `${locationId}-${stationId}`;
    return deletedState[key] !== undefined;
  };

  // Get current effective state of a station
  const getStationState = (locationId, stationId) => {
    const key = `${locationId}-${stationId}`;

    // Check deleted first
    if (deletedState[key]) {
      return { status: "deleted", price: null };
    }

    // Check updated
    if (updatedState[key]) {
      return { status: "updated", price: updatedState[key].price };
    }

    // Check default
    if (defaultState[key]) {
      return { status: "default", price: defaultState[key].price };
    }

    // Not involved
    return { status: "none", price: null };
  };

  // Filter locations based on search
  const filteredLocations = useMemo(() => {
    if (!searchQuery.trim()) return actualData;

    return actualData
      .map((location) => ({
        ...location,
        Stations: location.Stations.filter(
          (station) =>
            station.Name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            station.EvseId.toLowerCase().includes(searchQuery.toLowerCase())
        ),
      }))
      .filter((location) => location.Stations.length > 0);
  }, [actualData, searchQuery]);

  // Toggle edit mode for a station
  const toggleEditMode = (locationId, stationId) => {
    const key = `${locationId}-${stationId}`;
    setEditMode((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  // Handle price change
  const handlePriceChange = (locationId, stationId, value) => {
    const key = `${locationId}-${stationId}`;
    const price = parseFloat(value) || 0;

    // Remove from deleted state if present
    setDeletedState((prev) => {
      const newState = { ...prev };
      delete newState[key];
      return newState;
    });

    // Add/update in updated state
    setUpdatedState((prev) => ({
      ...prev,
      [key]: {
        locationId,
        stationId,
        price,
      },
    }));
  };

  // Toggle custom pricing for a station
  const toggleCustomPricing = (locationId, stationId, defaultPrice) => {
    const key = `${locationId}-${stationId}`;
    const currentState = getStationState(locationId, stationId);

    if (currentState.status === "none") {
      // Not involved - Add to updated state with default price
      setUpdatedState((prev) => ({
        ...prev,
        [key]: {
          locationId,
          stationId,
          price: defaultPrice,
        },
      }));

      // Automatically enable edit mode when switch is turned on
      setEditMode((prev) => ({
        ...prev,
        [key]: true,
      }));
    } else if (currentState.status === "default") {
      // In default state - Move to deleted state
      // Close edit mode if open
      setEditMode((prev) => {
        const newMode = { ...prev };
        delete newMode[key];
        return newMode;
      });

      setDeletedState((prev) => ({
        ...prev,
        [key]: {
          locationId,
          stationId,
        },
      }));
    } else if (currentState.status === "updated") {
      // In updated state - Check if it was in default originally
      // Close edit mode if open
      setEditMode((prev) => {
        const newMode = { ...prev };
        delete newMode[key];
        return newMode;
      });

      if (isInDefaultState(locationId, stationId)) {
        // Was in default - Move to deleted state
        setUpdatedState((prev) => {
          const newState = { ...prev };
          delete newState[key];
          return newState;
        });
        setDeletedState((prev) => ({
          ...prev,
          [key]: {
            locationId,
            stationId,
          },
        }));
      } else {
        // Was not in default - Just remove from updated
        setUpdatedState((prev) => {
          const newState = { ...prev };
          delete newState[key];
          return newState;
        });
      }
    } else if (currentState.status === "deleted") {
      // In deleted state - Restore to default state
      setDeletedState((prev) => {
        const newState = { ...prev };
        delete newState[key];
        return newState;
      });

      // Automatically enable edit mode when restoring custom pricing
      setEditMode((prev) => ({
        ...prev,
        [key]: true,
      }));
    }
  };

  // Save all changes
  const handleSaveChanges = async () => {
    try {
      // Group changes by location
      const locationChanges = {};

      // Process updated state
      Object.entries(updatedState).forEach(([key, change]) => {
        const { locationId, stationId, price } = change;

        if (!locationChanges[locationId]) {
          locationChanges[locationId] = {
            additions: [],
            removals: [],
            updates: [],
          };
        }

        // Check if it was in default state
        if (isInDefaultState(locationId, stationId)) {
          // Update existing custom pricing
          locationChanges[locationId].updates.push({
            Id: stationId,
            Price: price,
          });
        } else {
          // Add new custom pricing
          locationChanges[locationId].additions.push({
            Id: stationId,
            Price: price,
          });
        }
      });

      // Process deleted state
      Object.entries(deletedState).forEach(([key, change]) => {
        const { locationId, stationId } = change;

        if (!locationChanges[locationId]) {
          locationChanges[locationId] = {
            additions: [],
            removals: [],
            updates: [],
          };
        }

        // Remove custom pricing
        locationChanges[locationId].removals.push({
          Id: stationId,
        });
      });

      // Convert to API format
      const additions = [];
      const removals = [];
      const updates = [];

      Object.entries(locationChanges).forEach(([locationId, changes]) => {
        if (changes.additions.length > 0) {
          additions.push({
            Id: parseInt(locationId),
            Stations: changes.additions,
          });
        }
        if (changes.removals.length > 0) {
          removals.push({
            Id: parseInt(locationId),
            Stations: changes.removals,
          });
        }
        if (changes.updates.length > 0) {
          updates.push({
            Id: parseInt(locationId),
            Stations: changes.updates,
          });
        }
      });

      // Send to API
      await updatePricing({
        data: {
          Additions: additions,
          Removals: removals,
          Updates: updates,
        },
      });

      toast({
        title: "Pricing updated successfully",
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      // Clear changes and edit mode
      setUpdatedState({});
      setDeletedState({});
      setEditMode({});

      // Refetch data to refresh default state
      if (onRefresh) {
        await onRefresh();
      }
    } catch (error) {
      console.error("Error updating pricing:", error);
      toast({
        title: "Error updating pricing",
        description: error?.response?.data?.error || "Please try again",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  // Discard changes
  const handleDiscardChanges = () => {
    setUpdatedState({});
    setDeletedState({});
    setEditMode({});
    toast({
      title: "Changes discarded",
      status: "info",
      duration: 2000,
      isClosable: true,
    });
  };

  // Get display price for a station (for custom price column)
  const getDisplayPrice = (locationId, stationId, defaultPrice) => {
    const state = getStationState(locationId, stationId);

    // If deleted, return 0
    if (state.status === "deleted") {
      return 0;
    }

    // If there are pending changes (updated state), show the new price
    if (state.status === "updated") {
      return state.price;
    }

    // If in default state (has custom pricing from backend), show that price
    if (state.status === "default") {
      return state.price;
    }

    // Otherwise (none), use the station's default price
    return defaultPrice;
  };

  // Check if there are unsaved changes
  const hasUnsavedChanges =
    Object.keys(updatedState).length > 0 ||
    Object.keys(deletedState).length > 0;

  // Expand/Collapse all accordion items
  const expandAll = () => {
    // Create array of indices [0, 1, 2, 3, ...] for all locations
    const allIndices = actualData.map((_, index) => index);
    setExpandedItems(allIndices);
  };

  const collapseAll = () => {
    setExpandedItems([]);
  };

  // Render props pattern - pass all state and handlers to children
  return children({
    // Data
    actualData,
    pricingData,
    filteredLocations,
    
    // Search
    searchQuery,
    setSearchQuery,
    
    // State checks
    isInDefaultState,
    isInUpdatedState,
    isInDeletedState,
    getStationState,
    
    // Edit mode
    editMode,
    toggleEditMode,
    
    // Pricing operations
    handlePriceChange,
    toggleCustomPricing,
    getDisplayPrice,
    
    // Save/Discard
    handleSaveChanges,
    handleDiscardChanges,
    hasUnsavedChanges,
    updateLoading,
    
    // State counts
    updatedCount: Object.keys(updatedState).length,
    deletedCount: Object.keys(deletedState).length,
    
    // Accordion controls
    expandedItems,
    setExpandedItems,
    expandAll,
    collapseAll,
  });
};

export default HL_ProfilePricing;
